import type { ReactNode } from "react";
import type { EstadoCuota } from "../lib/types";
import { ESTADO_CUOTA_LABEL } from "../lib/types";

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 p-8 text-slate-500">
      <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-brand-600" />
      {label && <span className="text-sm">{label}</span>}
    </div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="card flex flex-col items-center gap-1 p-10 text-center">
      <p className="font-semibold text-slate-700">{title}</p>
      {hint && <p className="text-sm text-slate-400">{hint}</p>}
    </div>
  );
}

export function StatCard({
  label,
  value,
  hint,
  tone = "brand",
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "brand" | "emerald" | "amber" | "rose" | "slate";
}) {
  const tones: Record<string, string> = {
    brand: "text-brand-600",
    emerald: "text-emerald-600",
    amber: "text-amber-600",
    rose: "text-rose-600",
    slate: "text-slate-600",
  };
  return (
    <div className="card p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${tones[tone]}`}>{value}</p>
      {hint && <p className="mt-0.5 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

const ESTADO_TONE: Record<EstadoCuota, string> = {
  pagado: "bg-emerald-100 text-emerald-700",
  pendiente: "bg-rose-100 text-rose-700",
  por_vencer: "bg-amber-100 text-amber-700",
  exento: "bg-slate-100 text-slate-600",
  beca: "bg-violet-100 text-violet-700",
  abono: "bg-sky-100 text-sky-700",
};

export function EstadoBadge({ estado }: { estado: EstadoCuota }) {
  return <span className={`badge ${ESTADO_TONE[estado]}`}>{ESTADO_CUOTA_LABEL[estado]}</span>;
}

export function Modal({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="card w-full max-w-lg max-h-[90vh] overflow-y-auto p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-bold">{title}</h3>
          <button onClick={onClose} className="btn-ghost h-8 w-8 !px-0" aria-label="Cerrar">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
